// Context Menu: popup/dropdown menu.
import React from 'react';
import { injectIntl, defineMessages } from 'react-intl';

import { REM_SIZE } from '../config.js';
import { IconCopy, IconTrash, IconRefresh, IconShare, IconForward, IconSave, IconSeen, IconBell, IconSave2, IconRetry, IconPin, IconHide, IconUnhide, IconBellOff } from '../icons/svg/index.js';
import Helpers from '../lib/helpers.js';
import { copyTextToClipboard, copyImageToClipboard } from '../lib/utils.js';
import { profile } from '../stores/storages/ProfileStorage.js';
import { TOPIC_ME } from '../tinode/config.js';
import { HIDE_TOPIC_ACTION, HIDE_TOPIC_SUB_ACTION, MESSAGE_ACTION_PIN, NODE_SUBTYPE_MESSAGE } from '../tinode/constants.js';
import { Tinode } from '../tinode/tinode.js'

const messages = defineMessages({
  info: {
    id: 'menu_item_info',
    defaultMessage: 'Info',
    description: 'Show extended topic information'
  },
  clear_messages: {
    id: 'menu_item_clear_messages',
    defaultMessage: 'Clear messages',
    description: 'Delete all messages'
  },
  clear_for_all: {
    id: 'menu_item_clear_messages_for_all',
    defaultMessage: 'Clear for All',
    description: 'Delete all message(s) for all members'
  },
  delete: {
    id: 'menu_item_delete',
    defaultMessage: 'Delete message',
    description: 'Delete selected messages'
  },
  delete_for_all: {
    id: 'menu_item_delete_for_all',
    defaultMessage: 'Recall',
    description: 'Delete selected message(s) for all members'
  },
  send_retry: {
    id: 'menu_item_send_retry',
    defaultMessage: 'Retry',
    description: 'Retry sending message'
  },
  mute: {
    id: 'menu_item_mute',
    defaultMessage: 'Mute',
    description: 'Turn off notifications'
  },
  unmute: {
    id: 'menu_item_unmute',
    defaultMessage: 'Unmute',
    description: 'Turn notifications on'
  },
  reply: {
    id: 'menu_item_reply',
    defaultMessage: 'Reply',
    description: 'Reply to message'
  },
  forward: {
    id: 'menu_item_forward',
    defaultMessage: 'Forward',
    description: 'Forward message'
  },
  edit: {
    id: 'menu_item_edit',
    defaultMessage: 'Edit',
    description: 'Edit message'
  },
  save: {
    id: 'menu_item_save',
    defaultMessage: 'Save',
    description: 'Save message'
  },
  topic_delete: {
    id: 'menu_item_delete_topic',
    defaultMessage: 'Delete',
    description: 'Delete entire topic'
  },
  topic_delete_warning: {
    id: 'topic_delete_warning',
    defaultMessage: 'Are you sure you want to delete this conversation? It cannot be undone.',
    description: 'Alert warning when deleting entire topic'
  },
  delete_messages_warning: {
    id: 'delete_messages_warning',
    defaultMessage: 'Are you sure you want to delete all messages for everyone? It cannot be undone.',
    description: 'Alert dialog warning when hard-deleting all messages.'
  },
  unblock: {
    id: 'menu_item_unblock',
    defaultMessage: 'Unblock',
    description: 'Unblock topic or user'
  },
  // Generic string suitable for either topic or user.
  block: {
    id: 'menu_item_block',
    defaultMessage: 'Block',
    description: 'Block topic or user'
  },
  topic_block_warning: {
    id: 'topic_block_warning',
    defaultMessage: 'Are you sure you want to block this conversation?',
    description: 'Alert warning when blocking a topic.'
  },
  member_delete: {
    id: 'menu_item_member_delete',
    defaultMessage: 'Remove',
    description: 'Remove user from topic'
  },
  archive: {
    id: 'menu_item_archive_topic',
    defaultMessage: 'Archive',
    description: 'Move topic from the list of active chats to archive'
  },
  unarchive: {
    id: 'menu_item_restore_topic',
    defaultMessage: 'Restore',
    description: 'Restore topic from archive'
  },
  menu_item_pin_topic: {
    id: 'menu_item_pin_topic',
    defaultMessage: 'Pin this conversation',
    description: 'Pin topic'
  },
  menu_item_un_pin_topic: {
    id: 'menu_item_un_pin_topic',
    defaultMessage: 'Unpin this conversation',
    description: 'Pin topic'
  },
  menu_item_hide_topic: {
    id: 'menu_item_hide_topic',
    defaultMessage: 'Hide conversation',
    description: 'Hide topic'
  },
  menu_item_unhide_topic: {
    id: 'menu_item_unhide_topic',
    defaultMessage: 'Unhide conversation',
    description: 'Hide topic'
  },
  edit_permissions: {
    id: 'menu_item_edit_permissions',
    defaultMessage: 'Edit permissions',
    description: 'Menu item [Edit permissions]'
  },
  clear_messages_warning: {
    id: 'clear_messages_warning',
    defaultMessage: 'Are you sure you want to clear all messages? It cannot be undone.',
    description: 'Alert dialog warning when deleting all messages.'
  },
  copy: {
    id: 'menu_item_copy',
    defaultMessage: 'Copy',
    description: 'Copy'
  },
  delete_message_popup: {
    id: 'delete_message_popup',
    defaultMessage: 'Delete messages',
    description: 'Alert dialog warning when delete message.'
  },
  delete_message_content: {
    id: 'delete_message_content',
    defaultMessage: 'Are you sure you want to delete this message?',
    description: 'Alert dialog warning when delete message.'
  },
  seen_message_popup: {
    id: 'seen_message_popup',
    defaultMessage: 'Seen by',
    description: 'See who has read the message'
  },
  rename_group_title: {
    id: 'rename_group_title',
    defaultMessage: 'Rename the group',
    description: 'Rename the group'
  },
  copy_success: {
    id: 'copy_success',
    defaultMessage: 'Copied successfully',
    description: 'Copied successfully'
  },
  copy_failed: {
    id: 'copy_failed',
    defaultMessage: 'Copied failed',
    description: 'Copied failed'
  },
  notify_topic_mute_toast: {
    id: 'notify_topic_mute_toast',
    defaultMessage: 'Notifications from {topicName} are now muted',
    description: 'Notifications from {topicName} are now muted'
  },
  notify_topic_unmute_toast: {
    id: 'notify_topic_unmute_toast',
    defaultMessage: 'Notifications from {topicName} are now unmuted',
    description: 'Notifications from {topicName} are now unmuted'
  },
  pin: {
    id: 'menu_item_pin',
    defaultMessage: 'Ghim message',
    description: 'Ghim message'
  },
  alert_unpin_title: {
    id: 'alert_unpin_title',
    defaultMessage: 'Confirm',
    description: 'Confirm'
  },
  alert_change_pin_message: {
    id: 'alert_change_pin_message',
    defaultMessage: 'Another content is being pinned. Confirm switching to pinning this message?',
    description: 'Another content is being pinned. Confirm switching to pinning this message?'
  },
  alert_max_pin_thread: {
    id: 'alert_max_pin_thread',
    defaultMessage: 'You can only pin up to 3 chats',
    description: 'You can only pin up to 3 chats'
  },
  dialog_hide_topic_title: {
    id: "dialog_hide_topic_title",
    defaultMessage: "Hide conversation",
    description: "Hide conversation",
  },
  hide_topic_first_warning: {
    id: "hide_topic_first_warning",
    defaultMessage: "The conversation will be hidden from the message list. Only you can find and reopen them using your personal PIN",
    description: "Hide conversation warning",
  },
  set_hide_topic_password_button: {
    id: "set_hide_topic_password_button",
    defaultMessage: "Set a PIN",
    description: "Set a PIN",
  },
  alert_pin_topic_success: {
    id: "alert_pin_topic_success",
    defaultMessage: "Pin the conversation successfully",
    description: "Pin the conversation successfully",
  },
  alert_unpin_topic_success: {
    id: "alert_unpin_topic_success",
    defaultMessage: "Unpin the conversation successfully",
    description: "Unpin the conversation successfully",
  }
});

class ContextMenu extends React.Component {
  constructor(props) {
    super(props);

    this.selfRef = React.createRef();

    const { formatMessage } = props.intl;

    this.handlePageClick = this.handlePageClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.showMessageSeenBy = this.showMessageSeenBy.bind(this);
    this.topicMuteNotification = this.topicMuteNotification.bind(this);
    this.topicUnmuteNotification = this.topicUnmuteNotification.bind(this);
    this.handlePinTopic = this.handlePinTopic.bind(this);

    // Preconfigured menu items.
    this.MenuItems = {
      'topic_info': {
        id: 'topic_info',
        title: formatMessage(messages.info),
        icon: <IconCopy />,
        handler: null
      },
      'messages_clear': {
        id: 'messages_clear',
        title: formatMessage(messages.clear_messages),
        icon: <IconTrash />,
        handler: (params, errorHandler) => {
          return props.onShowAlert(
            formatMessage(messages.clear_messages), // title
            formatMessage(messages.clear_messages_warning), // content
            (_ => { this.deleteMessages(true, false, params, errorHandler); }), // onConfirm
            null, // "OK"
            true, // Show Reject button
            null  // "Cancel"
          );
        }
      },
      'message_delete': {
        id: 'message_delete',
        title: formatMessage(messages.delete),
        icon: <IconTrash />,
        handler: (params, errorHandler) => {
          return props.onShowAlert(
            formatMessage(messages.delete_message_popup), // title
            formatMessage(messages.delete_message_content), // content
            (_ => { return this.deleteMessages(false, false, params, errorHandler); }),
            null, // "OK"
            true, // Show Reject button
            null  // "Cancel"
          );
        }
      },
      'message_seen': {
        id: 'message_seen',
        title: formatMessage(messages.seen_message_popup),
        icon: <IconSeen />,
        handler: (params, errorHandler) => {
          this.showMessageSeenBy(params)
        }
      },
      'rename_group': {
        id: 'rename_group',
        title: formatMessage(messages.rename_group_title),
        icon: <IconCopy />,
        handler: (params, errorHandler) => {
          this.showDialogRenameGroup(params)
        }
      },
      'message_delete_hard': {
        id: 'message_delete_hard',
        title: formatMessage(messages.delete_for_all),
        icon: <IconRefresh />,
        handler: (params, errorHandler) => {
          return this.deleteMessages(false, true, params, errorHandler, true);
        }
      },
      'menu_item_send_retry': {
        id: 'menu_item_send_retry',
        title: formatMessage(messages.send_retry),
        icon: <IconRetry />,
        handler: (params, errorHandler) => {
          return this.retryMessage(params, errorHandler);
        }
      },
      'menu_item_reply': {
        id: 'menu_item_reply',
        title: formatMessage(messages.reply),
        icon: <IconShare />,
        handler: (params, errorHandler) => {
          return this.replyToMessage(params, errorHandler);
        }
      },
      'menu_item_forward': {
        id: 'menu_item_forward',
        icon: <IconForward />,
        title: formatMessage(messages.forward),
        handler: _ => { } /* the action is taken directly in tinode-web */
      },
      'menu_item_edit': {
        id: 'menu_item_edit',
        title: formatMessage(messages.edit),
        icon: <IconCopy />,
        handler: (params, errorHandler) => {
          return this.editMessage(params, errorHandler);
        }
      },
      'menu_item_save': {
        id: 'menu_item_save',
        title: formatMessage(messages.save),
        icon: <IconSave2 />,
        handler: (params, errorHandler) => {
          return this.saveMessage(params, errorHandler);
        }
      },
      'topic_unmute': {
        id: 'topic_unmute',
        title: formatMessage(messages.unmute),
        icon: <IconBell />,
        handler: (params) => {
          this.topicPermissionSetter('+P', params);
          this.topicUnmuteNotification(params);
        }
      },
      'topic_mute': {
        id: 'topic_mute',
        title: formatMessage(messages.mute),
        icon: <IconBellOff />,
        handler: (params) => {
          this.topicPermissionSetter('-P', params)
          this.topicMuteNotification(params);
        }
      },
      // 'topic_unblock': {
      //   id: 'topic_unblock',
      //   title: formatMessage(messages.unblock),
      //   icon: <IconCopy />,
      //   handler: this.topicPermissionSetter.bind(this, '+JP')
      // },
      // 'topic_block': {
      //   id: 'topic_block',
      //   title: formatMessage(messages.block),
      //   icon: <IconCopy />,
      //   handler: (params, errorHandler) => {
      //     return props.onShowAlert(
      //       formatMessage(messages.block), // title
      //       formatMessage(messages.topic_block_warning), // content
      //       (_ => this.topicPermissionSetter('-JP', params, errorHandler).then(ctrl => {
      //         this.props.onTopicRemoved(params.topicName);
      //         return ctrl;
      //       })),
      //       null, // "OK"
      //       true, // Show Reject button
      //       null  // "Cancel"
      //     );
      //   }
      // },
      'topic_delete': {
        id: 'topic_delete',
        title: formatMessage(messages.topic_delete),
        icon: <IconTrash />,
        handler: (params, errorHandler) => {
          return props.onShowAlert(
            formatMessage(messages.topic_delete), // title
            formatMessage(messages.topic_delete_warning), // content
            (_ => {
              const topic = this.props.tinode.getTopic(params.topicName);
              if (!topic) {
                console.warn("Topic not found: ", params.topicName);
                return;
              }
              return topic.delMessagesAll(true).then(res => {
                const me = this.props.tinode.getMeTopic();
                me.onContactUpdate('del-last', topic.name)
              }).catch(err => {
                if (errorHandler) {
                  errorHandler(err.message, 'err');
                }
              });
            }),
            null, // "OK"
            true, // Show Reject button
            null  // "Cancel"
          );
        }
      },
      // 'topic_archive': {
      //   id: 'topic_archive',
      //   title: formatMessage(messages.archive),
      //   icon: <IconCopy />,
      //   handler: (params, errorHandler) => {
      //     const topic = this.props.tinode.getTopic(params.topicName);
      //     if (!topic) {
      //       console.warn("Topic not found: ", params.topicName);
      //       return;
      //     }
      //     return topic.archive(true).catch(err => {
      //       if (errorHandler) {
      //         errorHandler(err.message, 'err');
      //       }
      //     });
      //   }
      // },
      'topic_restore': {
        id: 'topic_restore',
        title: formatMessage(messages.unarchive),
        icon: <IconCopy />,
        handler: (params, errorHandler) => {
          const topic = this.props.tinode.getTopic(params.topicName);
          if (!topic) {
            console.warn("Topic not found: ", params.topicName);
            return;
          }
          return topic.archive(false).catch(err => {
            if (errorHandler) {
              errorHandler(err.message, 'err');
            }
          });
        }
      },
      'topic_un_pin': {
        id: 'topic_un_pin',
        title: formatMessage(messages.menu_item_un_pin_topic),
        icon: <IconPin />,
        handler: (params, errorHandler) => {
          this.handlePinTopic(false, params.topicName)
        }
      },
      'topic_pin': {
        id: 'topic_pin',
        title: formatMessage(messages.menu_item_pin_topic),
        icon: <IconPin />,
        handler: (params, errorHandler) => {
          this.handlePinTopic(true, params.topicName)
        }
      },
      'topic_unhide': {
        id: 'topic_unhide',
        title: formatMessage(messages.menu_item_unhide_topic),
        icon: <IconHide />,
        handler: (params, errorHandler) => {
          this.handleUnhideTopic(params.topicName)
        }
      },
      'topic_hide': {
        id: 'topic_hide',
        title: formatMessage(messages.menu_item_hide_topic),
        icon: <IconUnhide />,
        handler: (params, errorHandler) => {
          this.handleHideTopic(params.topicName)
        }
      },
      // menu_item_edit_permissions is defined elsewhere.
      'permissions': {
        id: 'permissions',
        title: formatMessage(messages.edit_permissions),
        icon: <IconCopy />,
        handler: null
      },
      'member_delete': {
        id: 'member_delete',
        title: formatMessage(messages.member_delete),
        icon: <IconCopy />,
        handler: (params, errorHandler) => {
          const topic = this.props.tinode.getTopic(params.topicName);
          if (!topic || !params.user) {
            console.warn("Topic or user not found: '" + params.topicName + "', '" + params.user + "'");
            return;
          }
          return topic.delSubscription(params.user).catch(err => {
            if (errorHandler) {
              errorHandler(err.message, 'err');
            }
          });
        }
      },
      'member_mute': {
        id: 'member_mute',
        icon: <IconCopy />,
        title: formatMessage(messages.mute),
        handler: this.topicPermissionSetter.bind(this, '-P')
      },
      'member_unmute': {
        id: 'member_unmute',
        icon: <IconCopy />,
        title: formatMessage(messages.unmute),
        handler: this.topicPermissionSetter.bind(this, '+P')
      },
      'member_block': {
        id: 'member_block',
        icon: <IconCopy />,
        title: formatMessage(messages.block),
        handler: this.topicPermissionSetter.bind(this, '-JP')
      },
      'member_unblock': {
        id: 'member_unblock',
        icon: <IconCopy />,
        title: formatMessage(messages.unblock),
        handler: this.topicPermissionSetter.bind(this, '+JP')
      },
      'menu_item_copy': {
        id: 'menu_item_copy',
        title: formatMessage(messages.copy),
        icon: <IconCopy />,
        handler: (params) => {
          console.log(params)
          if (params.msg.subtype === NODE_SUBTYPE_MESSAGE.IMAGE) {
            let imgUrl = null
            if (Array.isArray(params?.content?.ent) && params.content.ent.length > 0) {
              imgUrl = Helpers.getDownloadURL(params.content.ent[0]?.data?.ref)
            }
            copyImageToClipboard(imgUrl).then(() => {
              Helpers.showSuccessToasts(formatMessage(messages.copy_success));
            }).catch(e => {
              console.error(e)
              Helpers.showErrorToasts(formatMessage(messages.copy_failed));
            });;
          } else {
            copyTextToClipboard(params.content)
            Helpers.showSuccessToasts(formatMessage(messages.copy_success));
          }
        }
      },
      'menu_item_pin': {
        id: 'menu_item_pin',
        title: formatMessage(messages.pin),
        icon: <IconPin />,
        handler: (params) => {
          if (params.msg?.topic) {
            const topic = this.props.tinode.getTopicByName(params.msg?.topic);
            const { pinMessage } = topic;
            if (pinMessage && pinMessage.from && pinMessage.content) {
              this.props.onShowAlert(
                formatMessage(messages.alert_unpin_title), // title
                formatMessage(messages.alert_change_pin_message), // content
                (_ => {
                  this.props.tinode.sendPin(params.msg, MESSAGE_ACTION_PIN.PIN);
                }),
                null, // "OK"
                true, // Show Reject button
                null  // "Cancel"
              );
              return;
            }
          }
          this.props.tinode.sendPin(params.msg, MESSAGE_ACTION_PIN.PIN);
        }
      }
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handlePageClick, false);
    document.addEventListener('keyup', this.handleEscapeKey, false);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handlePageClick, false);
    document.removeEventListener('keyup', this.handleEscapeKey, false);
  }

  handlePageClick(e) {
    if (this.selfRef.current.contains(e.target)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    this.props.hide();
  }

  handleEscapeKey(e) {
    if (e.keyCode === 27) {
      this.props.hide();
    }
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.hide();
    let item = this.props.items[e.currentTarget.dataset.id];
    if (typeof item == 'string') {
      item = this.MenuItems[item];
    }

    if (!item) {
      console.error("Invalid menu item ID", e.currentTarget.dataset.id);
    } else {
      this.props.onAction(
        item.id,
        item.handler(this.props.params, this.props.onError),
        this.props.params);
    }
  }

  // Menu Actions

  deleteMessages(all, hard, params, errorHandler, recall = false) {
    const topic = this.props.tinode.getTopic(params.topicName);
    if (!topic) {
      console.warn("Topic not found: ", params.topicName);
      return;
    }

    // We don't know if the message is still pending (e.g. attachment is being uploaded),
    // so try cancelling first. No harm if we can't cancel.
    // The message can be cancelled if transmission to the server has not
    // started yet or if the message send has failed.
    if (!all && topic.cancelSend(params.seq)) {
      return;
    }

    window.logger("deleteMessages|params=", params)
    if (topic.shareImage && Array.isArray(topic.shareImage)) {
      topic.shareImage = topic.shareImage.filter(element => element.messageId !== params.msg.id)
    }
    if (topic.shareFile && Array.isArray(topic.shareFile)) {
      topic.shareFile = topic.shareFile.filter(element => element.messageId !== params.msg.id)
    }
    if (topic.shareLink && Array.isArray(topic.shareLink)) {
      topic.shareLink = topic.shareLink.filter(element => element.messageId !== params.msg.id)
    }
    topic.udpTopicInfo(topic);

    const promise = recall ?
      topic.recallMessage(params.seq, params.msg)
      :
      all ?
        topic.delMessagesAll(hard) :
        params.replace > 0 ?
          topic.delMessagesEdits(params.replace, hard) :
          topic.delMessagesList([params.seq], hard);

    return promise.catch(err => {
      if (errorHandler) {
        errorHandler(err.message, 'err');
      }
    });


  }

  handlePinTopic(isPin, topicName) {
    const pinCount = profile.pinThreadCount
    const { formatMessage } = this.props.intl;
    if (isPin && pinCount >= 3) {
      Helpers.showWarningToasts(formatMessage(messages.alert_max_pin_thread))
    } else {
      const topic = this.props.tinode.getTopicByName(topicName)
      if (topic) {
        if (isPin) {
          const newPinThreadCount = profile.pinThreadCount + 1
          profile.pinThreadCount = newPinThreadCount > 3 ? 3 : newPinThreadCount
        } else {
          const newPinThreadCount = profile.pinThreadCount - 1
          profile.pinThreadCount = newPinThreadCount < 0 ? 0 : newPinThreadCount
        }

        const topicMe = this.props.tinode.getTopicByName(TOPIC_ME)
        const sub = {
          topic: topicName,
          isPin: isPin,
          updated: topic.updated,
        };
        topicMe._processMetaSubs([sub])

        if (isPin) {
          Helpers.showSuccessToasts(formatMessage(messages.alert_pin_topic_success))
        } else {
          Helpers.showSuccessToasts(formatMessage(messages.alert_unpin_topic_success))
        }
      }
    }
  }

  handleHideTopic(topicName) {
    const data = {
      topicName: topicName,
    }
    let action = HIDE_TOPIC_ACTION.SET_NEW_PASSWORD
    let isShowWarning = true
    if (profile.settings && profile.settings.hide_thread && profile.settings.hide_thread.pin) {
      action = HIDE_TOPIC_ACTION.VERIFY_PASSWORD
      isShowWarning = false
    }
    data.action = action
    data.subAction = HIDE_TOPIC_SUB_ACTION.HIDE


    if (isShowWarning) {
      const { formatMessage } = this.props.intl;
      this.props.onShowAlert(
        formatMessage(messages.dialog_hide_topic_title), // title
        formatMessage(messages.hide_topic_first_warning), // content
        (_ => this.props.onShowDialog(Tinode.DIALOG_TYPE.HIDE_TOPIC, data)),
        formatMessage(messages.set_hide_topic_password_button), // "OK"
        true, // Show Reject button
        null  // "Cancel"
      );
    } else {
      this.props.onShowDialog(Tinode.DIALOG_TYPE.HIDE_TOPIC, data)
    }
  }


  handleUnhideTopic(topicName) {
    const data = {
      topicName: topicName,
    }

    data.action = HIDE_TOPIC_ACTION.VERIFY_PASSWORD
    data.subAction = HIDE_TOPIC_SUB_ACTION.UNHIDE

    this.props.onShowDialog(Tinode.DIALOG_TYPE.HIDE_TOPIC, data)
  }



  showMessageSeenBy(params) {
    this.props.onShowDialog(Tinode.DIALOG_TYPE.MESSAGE_SEEN_BY, { msg: params.msg })
  }

  showDialogRenameGroup(params) {
    this.props.onShowDialog(Tinode.DIALOG_TYPE.RENAME_GROUP, { msg: params.msg })
  }
  //Function Mute notifications
  topicMuteNotification(params) {
    const { formatMessage } = this.props.intl;
    const topic = this.props.tinode.getTopic(params.topicName);
    if (!topic) {
      console.warn("Topic not found: ", params.topicName);
      return;
    }
    const topicName = topic.private && topic.private.comment ? topic.private.comment : topic.public.fn
    if (topicName) {
      Helpers.showSuccessToasts(formatMessage(messages.notify_topic_mute_toast, { topicName }));
    }
  }
  //Function Unmute notifications
  topicUnmuteNotification(params) {
    const { formatMessage } = this.props.intl;
    const topic = this.props.tinode.getTopic(params.topicName);
    if (!topic) {
      console.warn("Topic not found: ", params.topicName);
      return;
    }
    const topicName = topic.private && topic.private.comment ? topic.private.comment : topic.public.fn
    if (topicName) {
      Helpers.showSuccessToasts(formatMessage(messages.notify_topic_unmute_toast, { topicName }));
    }
  }
  // Retries sending failed message.
  retryMessage(params, errorHandler) {
    if (params.msg) {
      const topic = this.props.tinode.getTopicByName(params.topicName);
      // Remove the existing message entry.
      if (!topic || !topic.flushMessage(params.seq)) {
        return;
      }
      this.props.sendMessageToTopic(topic, params.msg.content, null, null, params.msg.head)
    }
  }

  // Function is used by context menu to set permissions.
  topicPermissionSetter(mode, params, errorHandler) {
    const topic = this.props.tinode.getTopic(params.topicName);
    if (!topic) {
      console.warn("Topic not found", params.topicName);
      return;
    }

    let result = topic.updateMode(params.user, mode);
    if (errorHandler) {
      result = result.catch(err => errorHandler(err.message, 'err'));
    }
    return result;
  }

  replyToMessage(params, errorHandler) {
    params.pickReply(params.msg, params.seq, params.content, params.userFrom, params.userName, errorHandler);
  }

  editMessage(params, errorHandler) {
    params.editMessage(params.replace || params.seq, params.content, errorHandler);
  }

  saveMessage(params, errorHandler) {
    params.saveMessage(params.msg, errorHandler);
  }

  render() {
    const menu = [];
    let count = 0;

    this.props.items.map((item) => {
      if (typeof item == 'string') {
        item = this.MenuItems[item];
      }
      if (item && item.title) {
        menu.push(
          item.title == '-' ?
            <li className="separator" key={count} />
            :
            <li onClick={this.handleClick} data-id={count} key={count}>
              <div>{item.icon}</div>
              {item.title}
            </li>
        );
      }
      count++;
    });

    // Ensure that menu is inside the app-container.
    const hSize = 12 * REM_SIZE;
    const vSize = REM_SIZE * (0.7 + menu.length * 2.5);
    const left = (this.props.bounds.right - this.props.clickAt.x < hSize) ?
      (this.props.clickAt.x - this.props.bounds.left - hSize) :
      (this.props.clickAt.x - this.props.bounds.left);
    const top = (this.props.bounds.bottom - this.props.clickAt.y < vSize) ?
      (this.props.clickAt.y - this.props.bounds.top - vSize) :
      (this.props.clickAt.y - this.props.bounds.top);

    const position = {
      left: left + 'px',
      top: top + 'px'
    };

    return (
      <ul className="menu" style={position} ref={this.selfRef}>
        {menu}
      </ul>
    );
  }
}

export default injectIntl(ContextMenu);
